
import { Bounce, Power1, Power3,Power2, gsap, Back} from "gsap";
import { BLEND_MODES, Text } from "pixi.js";
import { IDimOrientation, LayoutDimData, ResizeHelper } from "../../../core/utils/ResizeHelper";
import { BitmapTextStyleOptions } from "../../../core/setting/text/IText";
import { StageLayer } from '@nolimitcity/slot-launcher/bin/StageLayer';
import { MathHelper } from "../../../core/utils/MathHelper";
import { Helper } from "../../../core/utils/Helper";
import { SoundConfig } from "../../config/sound/SoundConfig";
import { AnimationEffects } from "../../../core/effects/AnimationEffects";
import { BitmapCountUp } from "../../../core/component/BitmapCountUp";
import { TextStyleConfig } from "../../config/translate/TextStyleConfig";
import { SanQuentin2 } from "../../SanQuentin2";
import { TranslationConfig } from "../../config/translate/TranslationConfig";
import { SoundManager } from "../../config/sound/SoundManager";
import { GameConfig } from "../../config/base/GameConfig";
import { GameSetting } from "../../../core/setting/GameSetting";

export class SanCounter extends PIXI.Container implements StageLayer {

    protected readonly counterDim:IDimOrientation = {
        landscape :{
            scale:      new PIXI.Point( 1, 1),
            position:   new PIXI.Point( 350, 250),
        },
        portrait :{
            scale:      new PIXI.Point( 1, 1),
            position:   new PIXI.Point( 360, 200)
        }
    };

    public get dim():LayoutDimData{ return ResizeHelper.calcDim(this.counterDim); };

    protected _abortTimeline:gsap.core.Timeline | null;
    protected _textPost:Text;

    public isBigWinPos:boolean = false;
    public isCapWinPos:boolean = false;

    protected _countUp: BitmapCountUp;
    protected _waysTxt: PIXI.Text;

    protected _timeline: gsap.core.Timeline;
    protected bounceTl: gsap.core.Timeline;

    private downDistanceBigWin: number = 240;
    private downDistance: number = 150;

    public get timeRemaining(): number {
        return this._timeline && this._timeline.isActive() ? this._timeline.duration() - this._timeline.totalTime() : 0;
    }

    private set waysText(value: number) {
        this._waysTxt.text = value.toString() + " " + GameSetting.translate(TranslationConfig.WAY_TEXT(value));
        this._waysTxt.updateTransform();
    }

    constructor() {
        super();
        this.name = "counter";
        this.init();
    }

    protected init(): void {

        this._waysTxt = this.addChild(new PIXI.Text("Ways"));
        this._waysTxt.alpha = 0;
        this._waysTxt.style = TextStyleConfig.XWAYS_WIN;
        this._waysTxt.anchor.set(0.5, 0.5);
        this._waysTxt.position.set(0, GameConfig.DEFAULT_SYMBOL_DIM.HEIGHT);
        this._waysTxt.pivot.x = -6;

        this._countUp = this.addChild(new BitmapCountUp(TextStyleConfig.BIT_COUNTER_WIN));
        this._countUp.alpha = 0;
        this._countUp.anchor = new PIXI.Point(0.5, 0.5);

        this.onResize();
    }
     
    public playBigWin(from: number, to: number, duration: number, ways: number = 1): gsap.core.Timeline {
        this._timeline = Helper.setTimeline(this._timeline!, { onComplete: () => this.countUpAnimVal(to) });

        const deciNumber: number = MathHelper.floorToDecimals(to, 2);
        this._timeline.add(this._countUp.getCountUpAnimation(from, deciNumber, { duration: duration, ease:Power2.easeInOut as any }), 0);
        if (from == 0) {
            this._timeline.fromTo(this._countUp, { alpha: 0 }, { duration: duration, alpha: 1, ease:Power2.easeInOut as any }, 0);
            if (duration){
                this._timeline.fromTo(this._countUp.scale, { x: 0.78, y: 0.78 }, { x: 1, y: 1, duration: duration-1, ease:Power2.easeInOut as any }, 0);
            }
        
        }else{
            this._countUp.alpha = 1;
        }
        this._timeline.add(
            AnimationEffects.Shake({
                x: 1.5,
                y: 1.5,
                obj: this._countUp,
                hertz: 80,
                duration: 0.5,
                updateOnComplete:true,
                positionCb:() => new PIXI.Point(0),
                Intensity: 2.75,
                ease: Power2.easeOut
            }).play()
        ,"-=0.5");
        this._timeline.to(this._countUp.scale, { x: 0.78, y: 0.78, duration: 1, ease:Power3.easeIn as any }, "-=1");
        this.waysText = ways;

        return this._timeline;
    }

    public play(from: number, to: number, duration: number): gsap.core.Timeline {
        this._timeline = Helper.setTimeline(this._timeline!, { onComplete: () => this.countUpAnimVal(to) });
        this.onResize();
        const deciNumber: number = MathHelper.floorToDecimals(to, 2);
        this._timeline.add(this._countUp.getCountUpAnimation(from, deciNumber, { duration: duration, ease: from ? Power2.easeInOut : Power2.easeIn  }), 0);

        if (from == 0) {
            this._timeline.fromTo(this._countUp, { alpha: 0 }, { duration: duration * 0.8, alpha: 1 }, 0);
            if (duration)
                this._timeline.fromTo(this._countUp.scale, { x: 0.72, y: 0.72 }, { x: 1, y: 1, duration: duration, ease: Power2.easeIn as any }, 0);
        }
        else this._countUp.alpha = 1;

        return this._timeline;
    }

    private countUpAnimVal(val: number): void {
        this._countUp.showValue(val);
    }

    public setValue(val?: number, ways?: number): void {
        if(val != undefined) this._countUp.showValue(val);
        if(ways != undefined) this.waysText = ways;
        this._countUp.alpha = this._waysTxt.alpha = 1;
    }

    private waysTL: gsap.core.Timeline;
    public showWays(duration: number = 0.65,wayAmount? :number): gsap.core.Timeline {
        if(wayAmount) this.waysText = wayAmount;
        this.waysTL = Helper.setTimeline(this.waysTL, {}, true);
        if(duration){
            this.waysTL.fromTo(this._waysTxt, { alpha: 0 }, { alpha: 1, duration: duration }, 0)
            this.waysTL.fromTo(this._waysTxt, { y: this._countUp.y }, { y: this._countUp.y + this.downDistance, duration: duration, ease:Back.easeOut as any }, 0)
        }else{
            this._waysTxt.alpha = 1;
            this._waysTxt.position.y = this._countUp.y + this.downDistance;
        }
        return this.waysTL;
    }

    public bounceHit(intencity: number = 0, durr: number = 0.2): gsap.core.Timeline {
        this.bounceTl = Helper.setTimeline(this.bounceTl);
        if (!intencity) return this.bounceTl;

        intencity = Math.min(intencity, 6);
        const inc: number = intencity * 0.05;

        this.bounceTl.to(this._countUp.scale, { duration: durr / 2, x: 1.15 + inc, y: 1.15 + inc }, 0);
        this.bounceTl.to(this._countUp.scale, { duration: durr, x: 1, y: 1, ease: Bounce.easeOut as any }, durr / 2);

        return this.bounceTl;
    }

    private movementTL: gsap.core.Timeline;
    public moveDown(duration: number = 0.5): gsap.core.Timeline {
        this.movementTL = Helper.setTimeline(this.movementTL);
        this.isBigWinPos = true;
        return this.movementTL.fromTo(this.position,{y:this.dim.position.y}, { y: this.dim.position.y + this.downDistanceBigWin, duration: duration, ease: Back.easeIn as any });
    }

    public moveToReset(durr: number):void {
        this.isBigWinPos = false;
    }

    // public moveToReset(durr: number): gsap.core.Timeline {
    //     this.isBigWinPos = false;
    //     this.movementTL = Helper.setTimeline(this.movementTL);
    //     this.movementTL.to(this.scale, { x: this.dim.scale.x, y: this.dim.scale.y, duration: durr },0)
    //     return this.movementTL.to(this.position, { y: this.dim.position.y, x: this.dim.position.x, duration: durr, ease: Power3.easeOut as any},0);
    // }


    public stop(endValue?: number, duration: number = 0.5, delay: number = 1, useLastAlpha: boolean = false): gsap.core.Timeline {
        this._timeline = Helper.setTimeline(this._timeline,{onComplete:() => this.onResize()});
        this.waysTL = Helper.setTimeline(this.waysTL);

        if (this._countUp.alpha == 0) return this._timeline;
        if (endValue) this._countUp.showValue(endValue);

        let startAlpha: number = useLastAlpha ? this._countUp.alpha : 1;
        let startAlphaWays: number = useLastAlpha ? this._waysTxt.alpha : 1;

        if(useLastAlpha) duration = Math.max(0.01, duration*this._countUp.alpha);

        this._timeline.fromTo(this._countUp, { alpha: startAlpha }, { delay: delay, duration: duration, alpha: 0 }, 0);
        this._timeline.fromTo(this._waysTxt, { alpha: startAlphaWays }, { delay: delay, duration: duration, alpha: 0 }, 0);
        this._timeline.play(0.001);
        return this._timeline;
    }

    public onResize(): void {
        this.position = this.dim.position;
        this.position.y += (this.isBigWinPos ? this.downDistanceBigWin : 0);
    
        this.position.y += (this.isCapWinPos ? this.downDistanceBigWin + (ResizeHelper.isLandscape ? 50 : 0) : 0);
        this.scale = this.dim.scale;
    }
}
